import type { ScoreUnit } from '../types';

const sign = (value: number, magnitude: string): string =>
  value >= 0 ? `+${magnitude}` : `-${magnitude}`;

export function formatProfMove(prev: number, curr: number): string {
  return `${prev.toFixed(1)} → ${curr.toFixed(1)} prof level`;
}

export function formatScaleScoreDelta(delta: number): string {
  return `${sign(delta, String(Math.abs(delta)))} scale-score pts`;
}

export function formatProfDelta(delta: number): string {
  return `${sign(delta, Math.abs(delta).toFixed(1))} prof level`;
}

export function formatYoYScaleScore(lastYearDelta: number): string {
  return `vs ${sign(lastYearDelta, String(Math.abs(lastYearDelta)))} last yr`;
}

export function formatRate(rate: number | null): string {
  if (rate === null) return '—';
  return `${Math.round(rate * 100)}%`;
}

export function formatPpDelta(thisRate: number, lastRate: number): string {
  const pp = Math.round((thisRate - lastRate) * 100);
  return `${sign(pp, String(Math.abs(pp)))} pp vs last year`;
}

export function formatCountWithDelta(count: number, delta: number | null): string {
  if (delta === null) return `${count}`;
  return `${count} (${sign(delta, String(Math.abs(delta)))})`;
}

/**
 * A year-over-year count move, framed without naming the prior year: `+59 vs last year`.
 * Uses a typographic minus (U+2212) rather than a hyphen, as the mockup sets it.
 */
export function formatYoYDelta(delta: number): string {
  const magnitude = Math.abs(delta);
  return `${delta >= 0 ? '+' : '−'}${magnitude} vs last year`;
}

/**
 * A headline count as a share of the active-EL total: `29% of active ELs`.
 * Null when there is no total to divide by — the card then shows no second line.
 */
export function formatShareOfActiveEls(count: number, total: number): string | null {
  if (total <= 0) return null;
  return `${Math.round((count / total) * 100)}% of active ELs`;
}

export function formatSignedProfDelta(delta: number): string {
  return sign(delta, Math.abs(delta).toFixed(1));
}

export function formatSignedScaleDelta(delta: number): string {
  return sign(delta, String(Math.abs(delta)));
}

function growthVsLastYear(
  thisDelta: number,
  lastDelta: number | null,
  fmt: (delta: number) => string,
): string | null {
  if (lastDelta === null) return null;
  if (thisDelta > lastDelta) return `Up from ${fmt(lastDelta)} last year`;
  if (thisDelta < lastDelta) return `Down from ${fmt(lastDelta)} last year`;
  return 'Same as last year';
}

export function compositeHeadlineSubline(args: {
  unit: ScoreUnit;
  profDelta: number | null;
  profDeltaLastYear: number | null;
  scaleScoreDelta: number | null;
  scaleScoreDeltaLastYear: number | null;
}): { headline: string; subline: string | null } {
  const { unit, profDelta, profDeltaLastYear, scaleScoreDelta, scaleScoreDeltaLastYear } = args;

  if (unit === 'scale_score' && scaleScoreDelta !== null) {
    return {
      headline: formatSignedScaleDelta(scaleScoreDelta),
      subline: growthVsLastYear(scaleScoreDelta, scaleScoreDeltaLastYear, formatSignedScaleDelta),
    };
  }

  if (profDelta === null) {
    return { headline: '—', subline: null };
  }
  return {
    headline: formatSignedProfDelta(profDelta),
    subline: growthVsLastYear(profDelta, profDeltaLastYear, formatSignedProfDelta),
  };
}
